import {
  LEVERAGE_MAX,
  LEVERAGE_MIN,
  LEVERAGE_STEPS,
} from "../constants";

const LAST_INDEX = LEVERAGE_STEPS.length - 1;

/**
 * Maps a leverage value to a 0–100 track position (linear between steps).
 * @param {number|string} value
 * @returns {number}
 */
export const leverageToPosition = (value) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric) || numeric <= LEVERAGE_MIN) return 0;
  if (numeric >= LEVERAGE_MAX) return 100;

  for (let i = 0; i < LAST_INDEX; i += 1) {
    const start = LEVERAGE_STEPS[i];
    const end = LEVERAGE_STEPS[i + 1];

    if (numeric >= start && numeric <= end) {
      return ((i + (numeric - start) / (end - start)) / LAST_INDEX) * 100;
    }
  }

  return 100;
};

/**
 * Snaps a 0–100 track position to the nearest leverage step.
 * @param {number} positionPercent
 * @returns {number}
 */
export const positionToNearestStep = (positionPercent) => {
  const clamped = Math.min(100, Math.max(0, positionPercent));
  const index = Math.round((clamped / 100) * LAST_INDEX);

  return LEVERAGE_STEPS[Math.min(LAST_INDEX, Math.max(0, index))];
};

/**
 * Index of the nearest step for a leverage value.
 * @param {number|string} value
 * @returns {number}
 */
export const nearestStepIndex = (value) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric) || numeric <= LEVERAGE_MIN) return 0;
  if (numeric >= LEVERAGE_MAX) return LAST_INDEX;

  let bestIndex = 0;
  let bestDistance = Infinity;

  for (let i = 0; i <= LAST_INDEX; i += 1) {
    const distance = Math.abs(LEVERAGE_STEPS[i] - numeric);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = i;
    }
  }

  return bestIndex;
};

/**
 * Converts a pointer clientX to a 0–100 position on the track.
 * @param {number} clientX
 * @param {DOMRect|null|undefined} trackRect
 * @returns {number}
 */
export const clientXToPosition = (clientX, trackRect) => {
  if (!trackRect?.width) return 0;

  return ((clientX - trackRect.left) / trackRect.width) * 100;
};

/**
 * Clamps leverage to [LEVERAGE_MIN, LEVERAGE_MAX] as an integer.
 * @param {number|string} value
 * @returns {number}
 */
export const clampLeverage = (value) => {
  const numeric = Math.trunc(Number(value));

  if (!Number.isFinite(numeric)) return LEVERAGE_MIN;

  return Math.min(LEVERAGE_MAX, Math.max(LEVERAGE_MIN, numeric));
};

export const LEVERAGE_LAST_INDEX = LAST_INDEX;
